import { AsNullable, AsPartial } from 'bz-utilities/ts';
import type { Nullable } from 'bz-utilities/ts';
import omit from '../../functions/omit';
import pick from '../../functions/pick';

export type TGroupedEntries<T> = [string, T[]][];
export type {};

declare global {
  interface ObjectConstructor {
    asNullable<T extends object>(x: T): Nullable<T>;
    asPartial<T extends object>(x: T): Partial<T>;
    groupByEntries<K extends PropertyKey, V>(
      items: Iterable<V>,
      keySelector: (item: V, index: number) => K
    ): TGroupedEntries<V>;
    omit<T extends object, K extends keyof T>(target: T, ...keys: Array<K>): Omit<T, K>;
    pick<T extends object, K extends keyof T>(this: T, ...keys: Array<K>): Pick<T, K>;
  }

  interface Object {
    asNullable<T extends object>(this: T): Nullable<T>;
    asPartial<T extends object>(this: T): Partial<T>;
    bzOmit<T extends object, K extends keyof T>(this: T, ...keys: Array<K>): Omit<T, K>;
    bzPick<T extends object, K extends keyof T>(this: T, ...keys: Array<K>): Pick<T, K>;
    toFrozen<T extends object>(this: T): Readonly<T>;
  }
}

if (!Object.asNullable) {
  Object.defineProperty(Object, 'asNullable', {
    writable: false,
    enumerable: false,
    value: function <T extends object>(x: T): Nullable<T> {
      return AsNullable(x);
    }
  });
}

if (!Object.asPartial) {
  Object.defineProperty(Object, 'asPartial', {
    writable: false,
    enumerable: false,
    value: function <T extends object>(x: T): Partial<T> {
      return AsPartial(x);
    }
  });
}

if (!Object.omit) {
  Object.defineProperty(Object, 'omit', {
    writable: false,
    enumerable: false,
    value: function <T extends object, K extends keyof T>(target: T, ...keys: K[]): Omit<T, K> {
      return omit(target, ...keys);
    }
  });
}

if (!Object.pick) {
  Object.defineProperty(Object, 'pick', {
    writable: false,
    enumerable: false,
    value: function <T extends object, K extends keyof T>(target: T, ...keys: K[]): Pick<T, K> {
      return pick(target, ...keys);
    }
  });
}

if (!Object.groupByEntries) {
  Object.defineProperty(Object, 'groupByEntries', {
    writable: false,
    enumerable: false,
    value: function <K extends PropertyKey, V>(
      items: Iterable<V>,
      keySelector: (item: V, index: number) => K
    ): TGroupedEntries<V> {
      const grouped = Object.groupBy(items, keySelector) as Record<K, V[]>;
      return Object.entries<V[]>(grouped);
    }
  });
}
const { prototype } = Object;

if (!prototype.asNullable) {
  Object.defineProperty(prototype, 'asNullable', {
    writable: false,
    enumerable: false,
    value: function <T extends object>(this: T): Nullable<T> {
      return AsNullable(this);
    }
  });
}

if (!prototype.asPartial) {
  Object.defineProperty(prototype, 'asPartial', {
    writable: false,
    enumerable: false,
    value: function <T extends object>(this: T): Partial<T> {
      return AsPartial(this);
    }
  });
}

if (!prototype.bzOmit) {
  Object.defineProperty(prototype, 'bzOmit', {
    writable: false,
    enumerable: false,
    value: function <T extends object, K extends keyof T>(this: T, ...keys: Array<K>): Omit<T, K> {
      return omit(this, ...keys);
    }
  });
}

if (!prototype.bzPick) {
  Object.defineProperty(prototype, 'bzPick', {
    writable: false,
    enumerable: false,
    value: function <T extends object, K extends keyof T>(this: T, ...keys: K[]): Pick<T, K> {
      return pick(this, ...keys);
    }
  });
}

if (!prototype.toFrozen) {
  Object.defineProperty(prototype, 'toFrozen', {
    writable: false,
    enumerable: false,
    value: function <T extends object>(this: T): Readonly<T> {
      return Object.freeze(this);
    }
  });
}
